"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { sourced, unavailable } from "@/components/ui/transaction-review-contract";
import { confirmListingStep, createListing } from "@/features/finance/api";
import { classifyChainPost, readChainWorkId } from "@/features/finance/chain-settlement";
import { trackChainWork } from "@/features/finance/chain-work-dock";
import { currencyStateReason, useCurrencyBalance, useNetworkCurrencies } from "@/features/finance/currency-balance";
import { useExternalStepHandoff } from "@/features/finance/hooks/useExternalStepHandoff";
import { refreshQuietly } from "@/features/finance/place-offer";
import { toastApiError } from "@/lib/api-toast";
import { getErrorMessage } from "@/lib/error-message";
import { formatDecimal, isPositive, multiplyDecimalString, parseDecimal, parseIntegerInput } from "@/lib/decimal-amount";
import { formatDecimalString } from "@/lib/token-amount";
import type { TransactionRecord } from "@/lib/transaction-flow";
import { buildReviewRows } from "../review-rows";
import type { FlowModel, SharedFlowProps } from "./shared";
export type ListForSaleProps = SharedFlowProps & {
  networkName: string;
  networkId?: number;
  currencyId?: number;
  onClose: () => void;
  onSuccess: () => void;
};

/**
 * LIST FOR SALE — the legacy `ListForSaleFlow`, moved verbatim: the currency
 * resolved from the NFT's own network (a single-currency network preselected
 * from the returned record, never a literal id), Persian digits canonicalised
 * before any arithmetic or payload, the external wallet's approve-then-sign
 * rounds against the one `listing_id`, and a queued chain leg handed to the
 * dock (the dialog steps aside, the page refreshes).
 */
export function useListForSale(p: ListForSaleProps) {
  const {
    lang,
    assetName,
    assetImage,
    collectionName,
    tokenId,
    networkLabel,
    royaltyPercent,
    contractAddress,
    onRecord,
    networkName,
    networkId,
    currencyId,
    onClose,
    onSuccess
  } = p;
  const isFa = lang === "fa";
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(currencyId ? String(currencyId) : "");
  // Engaged only on `LISTING_APPROVE_REQUIRED` / `LISTING_SIGN_REQUIRED` — an
  // NFT held in a self-custody wallet. The custodial path below is unchanged.
  const handoff = useExternalStepHandoff(lang, "listing_id");
  /** Set once the chain leg has been handed to the dock, read right after to decide whether to step aside. */
  const handedToDock = useRef(false);
  const currencyState = useNetworkCurrencies(networkId);
  const {
    currencies
  } = currencyState;

  // A single-currency network is preselected from the real returned record —
  // never a literal id. Anything else stays unselected so the user chooses.
  useEffect(() => {
    if (currencies.length === 1) {
      setSelectedCurrencyId(String(currencies[0].id));
      return;
    }
    setSelectedCurrencyId(current => current && !currencies.some(c => String(c.id) === current) ? "" : current);
  }, [currencies]);
  const currency = currencies.find(c_0 => String(c_0.id) === selectedCurrencyId);
  const balanceState = useCurrencyBalance(currency, lang);

  // Persian or Arabic-Indic digits: everything downstream uses the CANONICAL
  // ASCII form (`Number("۱۲.۵")` is NaN). The field keeps what was typed.
  const parsedPrice = parseDecimal(price);
  const priceValid = Boolean(parsedPrice && isPositive(parsedPrice));
  const canonicalPrice = priceValid && parsedPrice ? formatDecimal(parsedPrice) : null;
  const parsedQuantity = parseIntegerInput(quantity);
  const quantityValid = parsedQuantity !== null && parsedQuantity >= 1;
  const quantityNumber = parsedQuantity ?? 0;
  const unitPrice = canonicalPrice;
  const total = canonicalPrice && quantityValid ? multiplyDecimalString(canonicalPrice, quantityNumber) : null;
  const priceError = price && !priceValid ? isFa ? "قیمت باید عددی بزرگ‌تر از صفر باشد." : "The price must be a number greater than zero." : undefined;
  const quantityError = quantity && !quantityValid ? isFa ? "تعداد باید عددی صحیح و حداقل ۱ باشد." : "Quantity must be a whole number of at least 1." : undefined;
  const blockedReason = !priceValid ? isFa ? "برای ادامه، یک قیمت بزرگ‌تر از صفر وارد کنید." : "Enter a price greater than zero to continue." : !quantityValid ? isFa ? "برای ادامه، تعداد را درست وارد کنید." : "Enter a valid quantity to continue." : !currency ? currencyStateReason(currencyState, isFa) ?? (isFa ? "برای ادامه یک ارز انتخاب کنید." : "Select a currency to continue.") : null;
  const rows = buildReviewRows({
    lang,
    perspective: "seller",
    assetName,
    collectionName,
    tokenId,
    quantity: quantityValid ? quantityNumber : null,
    unitPrice,
    total,
    currencySymbol: currency?.symbol?.toUpperCase() ?? null,
    currencyUnavailableReason: currencyStateReason(currencyState, isFa) ?? undefined,
    networkLabel,
    balance: balanceState.balance,
    balanceUnavailableReason: balanceState.reason || (isFa ? "لیست کردن از موجودی شما برداشت نمی‌کند." : "Listing does not draw on your balance."),
    royaltyPercent,
    expiration: unavailable(isFa ? "سرویس لیست کردن هیچ پارامتر انقضایی نمی‌پذیرد." : "The listing endpoint accepts no expiry parameter."),
    conditions: sourced(isFa ? "لیست تا زمانی که خریداری یا توسط شما لغو شود روی همین قیمت باقی می‌ماند." : "The listing stays at this price until it is bought or you cancel it.")
  });
  const onConfirm = async (requestId: string): Promise<TransactionRecord | null> => {
    if (!currency || !total || !canonicalPrice) throw new Error("blocked");
    const success = (): TransactionRecord => ({
      requestId,
      flow: "list-for-sale",
      outcome: "success",
      title: isFa ? "NFT برای فروش لیست شد" : "Listed for sale",
      description: `${formatDecimalString(total, {
        locale: lang,
        maxFractionDigits: 18
      })} ${currency.symbol?.toUpperCase() ?? ""} · ${isFa ? "تعداد" : "quantity"} ${quantityNumber}`,
      at: new Date().toISOString()
    });
    /** The request went through; the CHAIN has not reported yet — the words never say listed. */
    const submitted = (): TransactionRecord => ({
      requestId,
      flow: "list-for-sale",
      outcome: "success",
      title: isFa ? "درخواست لیست ثبت شد" : "Listing submitted",
      description: isFa ? "در انتظار تایید بلاکچین. نتیجه در کادر گوشه‌ی صفحه و روی همین صفحه نمایش داده می‌شود." : "Waiting for the blockchain. The card at the corner of the page follows it.",
      at: new Date().toISOString()
    });
    const failure = (description?: string): TransactionRecord => ({
      requestId,
      flow: "list-for-sale",
      outcome: "failure",
      title: isFa ? "لیست کردن انجام نشد" : "Listing did not go through",
      description: description ?? (isFa ? "این NFT لیست نشد و چیزی از دارایی شما تغییر نکرد. می‌توانید دوباره تلاش کنید." : "The NFT was not listed and nothing changed. You can try again."),
      at: new Date().toISOString()
    });
    try {
      // Mid-sequence: approve the NFT, then sign the sale order — always
      // against the `listing_id` the first POST returned.
      if (handoff.pending) {
        const result = await handoff.sign((listingId, payload) => confirmListingStep(contractAddress, tokenId, listingId, payload));
        if (!result) return null;
        if (result.outcome === "pending_confirmation") return null;
        toast.success(isFa ? "NFT با موفقیت لیست شد" : "NFT listed successfully");
        await refreshQuietly(onSuccess);
        return success();
      }
      const response = await createListing(contractAddress, tokenId, {
        // Resolved from this NFT's own network. No `?? 1` fallback.
        currency_id: Number(currency.id),
        // The canonical form, never the raw field value.
        price_per_item: Number(canonicalPrice),
        quantity: quantityNumber,
        network: networkName
      });
      const handed = handoff.receive(response);
      // A self-custody wallet: stay on the review, where the panel asks for the approval.
      if (handed.kind === "handoff") return null;
      if (handed.kind === "unusable") {
        toast.error(isFa ? "لیست کردن ناموفق بود" : "Failed to list NFT");
        return failure(isFa ? "سرور برای این کار به امضای کیف پول نیاز داشت اما چیزی برای امضا نفرستاد." : "The server required a wallet signature but sent nothing to sign.");
      }

      // `SUBMIT_LISTING`: the row is written and the chain work is queued — not live yet.
      if (classifyChainPost(response, "list") === "queued") {
        handedToDock.current = true;
        trackChainWork({
          mode: "list",
          title: assetName,
          image: assetImage,
          contractAddress,
          tokenId,
          lang,
          workId: readChainWorkId(response, "list")
        });
        await refreshQuietly(onSuccess);
        toast.success(isFa ? "درخواست لیست ثبت شد؛ در انتظار بلاکچین" : "Listing submitted — waiting for the blockchain");
        return submitted();
      }
      toast.success(isFa ? "NFT با موفقیت لیست شد" : "NFT listed successfully");
      await refreshQuietly(onSuccess);
      return success();
    } catch (error) {
      toastApiError(error, isFa ? "لیست کردن ناموفق بود" : "Failed to list NFT");
      // The receipt carries the server's own reason where it sent one.
      return failure(getErrorMessage(error, "") || undefined);
    }
  };
  const model: FlowModel = {
    lang,
    title: isFa ? "لیست برای فروش" : "List for sale",
    description: isFa ? "قیمت و تعداد را وارد کنید." : "Set the price and quantity.",
    rows,
    irreversibility: isFa ? "پس از لیست شدن، این NFT می‌تواند بلافاصله با همین قیمت خریداری شود و فروش انجام‌شده قابل برگشت نیست." : "Once listed, this NFT can be bought immediately at this price, and a completed sale cannot be reversed.",
    confirmLabel: handoff.continueLabel ?? (isFa ? "لیست کردن" : "List for sale"),
    confirmConsequence: isFa ? "فروش انجام‌شده قابل برگشت نیست" : "a completed sale cannot be reversed",
    // Once the wallet owes a round, the only block that can still apply is "no wallet connected".
    blockedReason: handoff.pending ? handoff.blockedReason : blockedReason,
    hasInput: true,
    handoff,
    networkLabel,
    onConfirm,
    // Only when the chain leg went to the dock.
    closeOnSuccess: () => handedToDock.current,
    onRecord,
    onClose: handoff.guardClose(onClose)
  };
  return {
    model,
    price,
    setPrice,
    quantity,
    setQuantity,
    selectedCurrencyId,
    setSelectedCurrencyId,
    currencies,
    currencyReason: currencyStateReason(currencyState, isFa),
    priceError,
    quantityError,
    symbol: currency?.symbol?.toUpperCase() ?? networkLabel
  };
}